// src/pages/Settings.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch, clearStoredCredentials, getAuthHeaders } from '../utils/api';

// ✅ FIXED: No trailing spaces in fallback URL
const API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018').trim();

function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('basic-details');
  const [activeSection, setActiveSection] = useState('business-info');
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form Data State (Basic Details)
  const [formData, setFormData] = useState({
    businessName: '',
    stateName: '',
    businessAddress: '',
    productsOfInterest: '',
    businessDescription: '',
    email: '',
    phoneNumber: ''
  });

  // KYC Document State
  const [kycDocuments, setKycDocuments] = useState({
    nin: { 
      file: null, 
      preview: null, 
      uploaded: false, 
      uploading: false,
      path: null,
      name: null
    },
    cac: { 
      file: null, 
      preview: null, 
      uploaded: false, 
      uploading: false,
      path: null,
      name: null
    }
  });

  // ✅ Brand Logo State - SAME STRUCTURE as NIN/CAC
  const [brandLogo, setBrandLogo] = useState({
    file: null,
    preview: null,
    uploaded: false,
    uploading: false,
    path: null,
    name: null
  });

  // ✅ Get Company ID helper
  const getCompanyId = () => {
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const id = location.state?.companyId 
      || session?.companyId 
      || session?.CompanyId 
      || location.state?.sessionId 
      || session?.sessionId 
      || null;
    return id ? String(id).trim() : null;
  };

  // ✅ Format phone number to +234XXXXXXXXXX format
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let cleaned = String(phone).replace(/[^\d+]/g, '');
    if (cleaned.startsWith('234')) cleaned = '+' + cleaned;
    if (cleaned.startsWith('0') && cleaned.length === 11) {
      cleaned = '+234' + cleaned.slice(1);
    }
    return cleaned;
  };

  // ✅ Validate KYC file before upload (PDF/JPG/PNG)
  const validateKycFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };
    
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      return { valid: false, error: 'Invalid file type. Allowed: PDF, JPG, PNG' };
    }
    
    if (file.size > 5 * 1024 * 1024) {
      return { valid: false, error: 'File size must be less than 5MB' };
    }
    
    return { valid: true, error: null };
  };

  // ✅ Validate image file for logo (JPG/PNG/GIF/SVG)
  const validateImageFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };
    
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      return { valid: false, error: 'Invalid file type. Allowed: JPG, PNG, GIF, SVG' };
    }
    
    if (file.size > 2 * 1024 * 1024) {
      return { valid: false, error: 'File size must be less than 2MB' };
    }
    
    return { valid: true, error: null };
  };

  // ✅ Upload using authenticatedFetch utility (from api.js)
  const uploadKycDocument = async (docType, file, companyId) => {
    const formData = new FormData();
    formData.append('companyId', companyId);
    formData.append('documentType', docType.toLowerCase());
    formData.append('file', file);

    const headers = getAuthHeaders();
    
    if (!headers) {
      throw new Error('Not authenticated. Please log in again.');
    }
    
    console.log('🔐 UPLOAD DEBUG:', {
      endpoint: `${API_BASE}/HMO/upload-company-document`,
      companyId,
      docType,
      fileName: file.name,
      fileSize: file.size,
      hasAuthHeaders: !!headers.AuthorizationKey && !!headers.Token && !!headers.SecretKey
    });

    const result = await authenticatedFetch(
      `${API_BASE}/HMO/upload-company-document`,
      'POST',
      formData,
      null
    );

    if (result?.status !== 'success') {
      console.error('❌ Upload failed:', result);
      throw new Error(result?.message || 'Upload failed');
    }

    return result;
  };

  // ✅ Fetch Business Data from API
  const fetchBusinessData = async () => {
    const companyId = getCompanyId();
    if (!companyId) return;

    try {
      const result = await authenticatedFetch(
        `${API_BASE}/HMO/get-company-by-session`, 
        'POST', 
        { sessionId: companyId.toString() }
      );

      if (result?.status === 'success' && result?.data) {
        const data = result.data;
        
        setFormData({
          businessName: data?.businessName || data?.BusinessName || '',
          stateName: data?.stateName || data?.StateName || data?.stateOfOperation || '',
          businessAddress: data?.businessAddress || data?.BusinessAddress || '',
          productsOfInterest: data?.productsOfInterest || data?.ProductsOfInterest || '',
          businessDescription: data?.businessDescription || data?.BusinessDescription || '',
          email: data?.email || data?.Email || '',
          phoneNumber: formatPhoneNumber(data?.phoneNumber || data?.PhoneNumber || data?.businessPhone || '')
        });

        // ✅ Load existing NIN document
        if (data?.NINPath || data?.ninDocument) {
          const path = data.NINPath || data.ninDocument;
          setKycDocuments(prev => ({
            ...prev,
            nin: { 
              ...prev.nin, 
              preview: path,
              uploaded: true,
              path: path,
              name: path?.split('/').pop() || 'NIN Document'
            }
          }));
        }
        
        // ✅ Load existing CAC document
        if (data?.CACPath || data?.cacDocument) {
          const path = data.CACPath || data.cacDocument;
          setKycDocuments(prev => ({
            ...prev,
            cac: { 
              ...prev.cac, 
              preview: path,
              uploaded: true,
              path: path,
              name: path?.split('/').pop() || 'CAC Document'
            }
          }));
        }

        // ✅ Load existing brand logo - SAME PATTERN as NIN/CAC
        if (data?.LogoPath || data?.logoPath || data?.Logo) {
          const path = data.LogoPath || data.logoPath || data.Logo;
          setBrandLogo({
            preview: path,
            uploaded: true,
            path: path,
            name: path?.split('/').pop() || 'Company Logo',
            file: null,
            uploading: false
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch business data:', err);
      toast.error('Could not load business details');
    }
  };

  // ✅ Check auth & init on mount
  useEffect(() => {
    const checkAuth = () => {
      const session = localStorage.getItem('userSession');
      if (!session) {
        toast.error('Please log in to continue');
        navigate('/login', { replace: true });
        return false;
      }
      return true;
    };
    
    if (checkAuth()) {
      AOS.init({ duration: 1000, once: true });
      fetchBusinessData();
    }
  }, [navigate]);

  // ✅ Navigation items
  const getNavItems = () => {
    const currentPath = location.pathname;
    return [
      { 
        section: 'MAIN', 
        items: [
          { name: 'Get Started', icon: 'fa-home', path: '/get-started', active: currentPath === '/get-started' }
        ] 
      },
      { 
        section: 'DASHBOARD', 
        items: [
          { name: 'Analytics', icon: 'fa-chart-bar', path: '/dashboard', active: currentPath === '/dashboard' }
        ] 
      },
      { 
        section: 'PRODUCTS', 
        items: [
          { name: 'Products', icon: 'fa-box', path: '/products', active: currentPath === '/products' },
          { name: 'Hmo Links', icon: 'fa-link', path: '/insurance-links', active: currentPath === '/insurance-links' }
        ]
      },
      { 
        section: 'TRANSACTIONS', 
        items: [
          { name: 'Wallets', icon: 'fa-wallet', path: '/wallets', active: currentPath === '/wallets' },
          { name: 'Audit Logs', icon: 'fa-clipboard-check', path: '/audit-logs', active: currentPath === '/audit-logs' }
        ]
      },
    ];
  };

  const navItems = getNavItems();

  // ✅ Settings tabs
  const settingsTabs = [
    { id: 'basic-details', label: 'Basic Details', icon: 'fa-id-card' },
    { id: 'password-security', label: 'Password & Security', icon: 'fa-shield-alt' }
  ];

  // ✅ Left menu items
  const leftMenuItems = [
    { id: 'business-info', label: 'Business Information', icon: 'fa-building' },
    { id: 'kyc-document', label: 'Business KYC/Document', icon: 'fa-file-alt' },
    { id: 'brand-element', label: 'Brand Element (optional)', icon: 'fa-paint-brush' }
  ];

  // Handlers - Basic Details
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdate = () => {
    console.log('Updating settings:', formData);
    setIsEditing(false);
    toast.success('Settings updated successfully!');
  };

  // ========================================
  // ✅ BRAND LOGO UPLOAD FUNCTIONS (SAME AS NIN/CAC)
  // ========================================

  const handleSelectLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = '';
      return;
    }

    // Create preview for images
    const preview = URL.createObjectURL(file);

    setBrandLogo(prev => ({
      ...prev,
      file,
      preview,
      uploaded: false,
      uploading: false
    }));
    
    toast.success('Logo file selected. Click "Upload Logo" to submit.');
  };

  const handleUploadLogo = async () => {
    const logo = brandLogo;
    
    if (!logo?.file) {
      toast.error('Please select a logo file first');
      return;
    }

    const companyId = getCompanyId();
    if (!companyId) {
      toast.error('Session expired. Please log in again.');
      return;
    }

    setBrandLogo(prev => ({
      ...prev,
      uploading: true
    }));

    try {
      // ✅ Uses same uploadKycDocument function with documentType='logo'
      const result = await uploadKycDocument('logo', logo.file, companyId);
      
      // Clean up old preview URL
      if (logo.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(logo.preview);
      }
      
      setBrandLogo({
        file: null,
        preview: result.path,
        uploaded: true,
        uploading: false,
        path: result.path,
        name: logo.file.name
      });
      
      toast.success('Company logo uploaded successfully!');
      fetchBusinessData();
      
    } catch (err) {
      console.error('Logo upload error:', err);
      setBrandLogo(prev => ({
        ...prev,
        uploading: false
      }));
      toast.error(`Failed to upload logo: ${err.message}`);
    }
  };

  const handleRemoveLogo = () => {
    const logo = brandLogo;
    
    // Clean up blob URL to prevent memory leaks
    if (logo.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(logo.preview);
    }
    
    setBrandLogo({
      file: null,
      preview: null,
      uploaded: false,
      uploading: false,
      path: null,
      name: null
    });
    
    toast.info('Logo removed');
  };

  // ========================================
  // ✅ NIN UPLOAD FUNCTIONS
  // ========================================

  const handleSelectNINFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateKycFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = '';
      return;
    }

    let preview = null;
    if (file.type.startsWith('image/')) {
      preview = URL.createObjectURL(file);
    }

    setKycDocuments(prev => ({
      ...prev,
      nin: { 
        ...prev.nin, 
        file, 
        preview, 
        uploaded: false,
        uploading: false
      }
    }));
    
    toast.success('NIN file selected. Click "Upload NIN" to submit.');
  };

  const handleUploadNIN = async () => {
    const doc = kycDocuments.nin;
    
    if (!doc?.file) {
      toast.error('Please select a NIN file first');
      return;
    }

    const companyId = getCompanyId();
    if (!companyId) {
      toast.error('Session expired. Please log in again.');
      return;
    }

    setKycDocuments(prev => ({
      ...prev,
      nin: { ...prev.nin, uploading: true }
    }));

    try {
      const result = await uploadKycDocument('nin', doc.file, companyId);
      
      if (doc.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(doc.preview);
      }
      
      setKycDocuments(prev => ({
        ...prev,
        nin: { 
          file: null,
          preview: result.path,
          uploaded: true,
          uploading: false,
          path: result.path,
          name: doc.file.name
        }
      }));
      
      toast.success('NIN document uploaded successfully!');
      fetchBusinessData();
      
    } catch (err) {
      console.error('NIN upload error:', err);
      setKycDocuments(prev => ({
        ...prev,
        nin: { ...prev.nin, uploading: false }
      }));
      toast.error(`Failed to upload NIN: ${err.message}`);
    }
  };

  const handleRemoveNIN = () => {
    const doc = kycDocuments.nin;
    
    if (doc.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(doc.preview);
    }
    
    setKycDocuments(prev => ({
      ...prev,
      nin: { file: null, preview: null, uploaded: false, uploading: false, path: null, name: null }
    }));
    
    toast.info('NIN document removed');
  };

  // ========================================
  // ✅ CAC UPLOAD FUNCTIONS
  // ========================================

  const handleSelectCACFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateKycFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = '';
      return;
    }

    let preview = null;
    if (file.type.startsWith('image/')) {
      preview = URL.createObjectURL(file);
    }

    setKycDocuments(prev => ({
      ...prev,
      cac: { 
        ...prev.cac, 
        file, 
        preview, 
        uploaded: false,
        uploading: false
      }
    }));
    
    toast.success('CAC file selected. Click "Upload CAC" to submit.');
  };

  const handleUploadCAC = async () => {
    const doc = kycDocuments.cac;
    
    if (!doc?.file) {
      toast.error('Please select a CAC file first');
      return;
    }

    const companyId = getCompanyId();
    if (!companyId) {
      toast.error('Session expired. Please log in again.');
      return;
    }

    setKycDocuments(prev => ({
      ...prev,
      cac: { ...prev.cac, uploading: true }
    }));

    try {
      const result = await uploadKycDocument('cac', doc.file, companyId);
      
      if (doc.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(doc.preview);
      }
      
      setKycDocuments(prev => ({
        ...prev,
        cac: { 
          file: null,
          preview: result.path,
          uploaded: true,
          uploading: false,
          path: result.path,
          name: doc.file.name
        }
      }));
      
      toast.success('CAC document uploaded successfully!');
      fetchBusinessData();
      
    } catch (err) {
      console.error('CAC upload error:', err);
      setKycDocuments(prev => ({
        ...prev,
        cac: { ...prev.cac, uploading: false }
      }));
      toast.error(`Failed to upload CAC: ${err.message}`);
    }
  };

  const handleRemoveCAC = () => {
    const doc = kycDocuments.cac;
    
    if (doc.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(doc.preview);
    }
    
    setKycDocuments(prev => ({
      ...prev,
      cac: { file: null, preview: null, uploaded: false, uploading: false, path: null, name: null }
    }));
    
    toast.info('CAC document removed');
  };

  // Navigation & Logout
  const handleNavClick = (path, e) => {
    e.preventDefault();
    if (path) navigate(path);
  };

  const handleLogout = (e) => {
    e.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('fsl_session');
    localStorage.removeItem('rememberedEmail');
    toast.success('Logged out');
    navigate('/login', { replace: true });
  };

  const getSectionTitle = () => {
    const section = leftMenuItems.find(item => item.id === activeSection);
    return section ? section.label : '';
  };

  // ✅ Render Basic Details Content
  const renderBasicDetailsContent = () => {
    switch (activeSection) {
      case 'business-info':
        return (
          <div className="settings-form" data-aos="fade-up">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Business name</label>
                <input 
                  type="text" name="businessName" className="form-control"
                  value={formData.businessName} onChange={handleInputChange} disabled={!isEditing}
                  placeholder="Enter business name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">State Name</label>
                <input 
                  type="text" name="stateName" className="form-control"
                  value={formData.stateName} onChange={handleInputChange} disabled={!isEditing}
                  placeholder="e.g., Lagos, Anambra, Abuja"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Business address</label>
                <input 
                  type="text" name="businessAddress" className="form-control"
                  value={formData.businessAddress} onChange={handleInputChange} disabled={!isEditing}
                  placeholder="Enter full business address"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Products of interest</label>
                <select 
                  name="productsOfInterest" className="form-control"
                  value={formData.productsOfInterest} onChange={handleInputChange} disabled={!isEditing}
                >
                  <option value="">Select product</option>
                  <option value="Health">Health</option>
                  <option value="Life">Life</option>
                  <option value="General">General</option>
                  <option value="Motor">Motor</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input 
                  type="email" className="form-control"
                  value={formData.email} 
                  disabled
                  placeholder="Email from registration"
                />
                <small className="text-muted">Email is managed during registration</small>
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input 
                  type="tel" className="form-control"
                  value={formData.phoneNumber} 
                  disabled
                  placeholder="+2347068420039"
                />
                <small className="text-muted">Phone is managed during registration</small>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Business description</label>
              <textarea 
                name="businessDescription" className="form-control textarea" rows="4"
                value={formData.businessDescription} onChange={handleInputChange} disabled={!isEditing}
                placeholder="Brief description of your business..."
              ></textarea>
            </div>
            <div className="info-box">
              <i className="fas fa-info-circle"></i>
              <p>Note: Email and Phone Number are set during registration. Contact support to update them.</p>
            </div>
            {isEditing && (
              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleUpdate}>Update</button>
                <button className="btn btn-secondary" onClick={() => setIsEditing(false)}>Cancel</button>
              </div>
            )}
          </div>
        );

      // ✅ KYC Document Section
      case 'kyc-document':
        return (
          <div className="settings-section" >
            <p className="text-muted mb-4">Upload your mandatory KYC documents for verification.</p>
            
            {/* ✅ NIN Document Upload Card */}
            <div className="document-upload-card mb-4">
              <div className="document-header">
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-id-card text-orange"></i>
                  <h6 className="mb-0">NIN Document</h6>
                </div>
                {kycDocuments.nin.uploaded && (
                  <span className="badge bg-success">✓ Uploaded</span>
                )}
              </div>
              <p className="text-muted small mb-3">Upload a clear copy of your National Identity Number (NIN) slip or card.</p>
              
              {!kycDocuments.nin.uploaded ? (
                <div className="upload-area">
                  {kycDocuments.nin.preview ? (
                    <div className="preview-container">
                      {kycDocuments.nin.preview.startsWith('blob:') ? (
                        <img src={kycDocuments.nin.preview} alt="NIN Preview" className="document-preview" />
                      ) : (
                        <div className="file-preview">
                          <i className="fas fa-file-pdf fa-3x text-danger"></i>
                          <p className="mt-2 mb-0 small">{kycDocuments.nin.file?.name || kycDocuments.nin.name}</p>
                        </div>
                      )}
                      <div className="d-flex gap-2 justify-content-center mt-2">
                        <button className="btn btn-sm btn-danger" onClick={handleRemoveNIN}>
                          <i className="fas fa-trash me-1"></i> Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt"></i>
                      <h6>Drag & drop or click to browse</h6>
                      <p className="text-muted small">PDF, JPG, PNG • Max 5MB</p>
                      <input 
                        type="file" 
                        id="nin-upload" 
                        className="d-none" 
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleSelectNINFile}
                      />
                      <label htmlFor="nin-upload" className="btn btn-secondary mt-2" style={{ cursor: 'pointer' }}>
                        <i className="fas fa-folder-open me-1"></i> Select NIN File
                      </label>
                    </>
                  )}
                  
                  {kycDocuments.nin.file && !kycDocuments.nin.uploaded && !kycDocuments.nin.uploading && (
                    <button 
                      className="btn btn-primary mt-3" 
                      style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}
                      onClick={handleUploadNIN}
                    >
                      <i className="fas fa-upload me-1"></i> Upload NIN
                    </button>
                  )}
                  
                  {kycDocuments.nin.uploading && (
                    <button className="btn btn-primary mt-3" disabled style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Uploading...
                    </button>
                  )}
                </div>
              ) : (
                <div className="uploaded-state text-center py-3">
                  <i className="fas fa-check-circle text-success fa-2x mb-2"></i>
                  <p className="mb-0 text-muted small">Document uploaded successfully</p>
                  {kycDocuments.nin.name && (
                    <small className="d-block text-muted mt-1">{kycDocuments.nin.name}</small>
                  )}
                  <button className="btn btn-sm btn-outline-secondary mt-2" onClick={handleRemoveNIN}>
                    <i className="fas fa-redo me-1"></i> Replace Document
                  </button>
                </div>
              )}
            </div>

            {/* ✅ CAC Document Upload Card */}
            <div className="document-upload-card">
              <div className="document-header">
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-file-contract text-orange"></i>
                  <h6 className="mb-0">CAC Document</h6>
                </div>
                {kycDocuments.cac.uploaded && (
                  <span className="badge bg-success">✓ Uploaded</span>
                )}
              </div>
              <p className="text-muted small mb-3">Upload your Corporate Affairs Commission (CAC) registration certificate.</p>
              
              {!kycDocuments.cac.uploaded ? (
                <div className="upload-area">
                  {kycDocuments.cac.preview ? (
                    <div className="preview-container">
                      {kycDocuments.cac.preview.startsWith('blob:') ? (
                        <img src={kycDocuments.cac.preview} alt="CAC Preview" className="document-preview" />
                      ) : (
                        <div className="file-preview">
                          <i className="fas fa-file-pdf fa-3x text-danger"></i>
                          <p className="mt-2 mb-0 small">{kycDocuments.cac.file?.name || kycDocuments.cac.name}</p>
                        </div>
                      )}
                      <div className="d-flex gap-2 justify-content-center mt-2">
                        <button className="btn btn-sm btn-danger" onClick={handleRemoveCAC}>
                          <i className="fas fa-trash me-1"></i> Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt"></i>
                      <h6>Drag & drop or click to browse</h6>
                      <p className="text-muted small">PDF, JPG, PNG • Max 5MB</p>
                      <input 
                        type="file" 
                        id="cac-upload" 
                        className="d-none" 
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleSelectCACFile}
                      />
                      <label htmlFor="cac-upload" className="btn btn-secondary mt-2" style={{ cursor: 'pointer' }}>
                        <i className="fas fa-folder-open me-1"></i> Select CAC File
                      </label>
                    </>
                  )}
                  
                  {kycDocuments.cac.file && !kycDocuments.cac.uploaded && !kycDocuments.cac.uploading && (
                    <button 
                      className="btn btn-primary mt-3" 
                      style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}
                      onClick={handleUploadCAC}
                    >
                      <i className="fas fa-upload me-1"></i> Upload CAC
                    </button>
                  )}
                  
                  {kycDocuments.cac.uploading && (
                    <button className="btn btn-primary mt-3" disabled style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Uploading...
                    </button>
                  )}
                </div>
              ) : (
                <div className="uploaded-state text-center py-3">
                  <i className="fas fa-check-circle text-success fa-2x mb-2"></i>
                  <p className="mb-0 text-muted small">Document uploaded successfully</p>
                  {kycDocuments.cac.name && (
                    <small className="d-block text-muted mt-1">{kycDocuments.cac.name}</small>
                  )}
                  <button className="btn btn-sm btn-outline-secondary mt-2" onClick={handleRemoveCAC}>
                    <i className="fas fa-redo me-1"></i> Replace Document
                  </button>
                </div>
              )}
            </div>

            <div className="info-box mt-4">
              <i className="fas fa-shield-alt"></i>
              <p className="mb-0">Your documents are encrypted and stored securely. They are only used for identity verification and regulatory compliance.</p>
            </div>
          </div>
        );

      // ✅ Brand Element Section - NOW WITH SAME UPLOAD PATTERN AS NIN/CAC
      case 'brand-element':
        return (
          <div className="settings-section" >
            <p className="text-muted mb-4">Customize your brand elements like logo and colors.</p>
            
            {/* ✅ Company Logo Upload Card - SAME STRUCTURE as NIN/CAC */}
            <div className="document-upload-card">
              <div className="document-header">
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-image text-orange"></i>
                  <h6 className="mb-0">Company Logo</h6>
                </div>
                {brandLogo.uploaded && (
                  <span className="badge bg-success">✓ Uploaded</span>
                )}
              </div>
              <p className="text-muted small mb-3">Upload your company logo. Recommended: PNG or SVG, max 2MB.</p>
              
              {!brandLogo.uploaded ? (
                <div className="upload-area">
                  {brandLogo.preview ? (
                    // ✅ Preview State - SAME as NIN/CAC
                    <div className="preview-container">
                      {brandLogo.preview.startsWith('blob:') ? (
                        <img src={brandLogo.preview} alt="Logo Preview" className="document-preview" style={{ maxWidth: '200px' }} />
                      ) : (
                        <img src={`${API_BASE}${brandLogo.preview}`} alt="Company Logo" className="document-preview" style={{ maxWidth: '200px' }} />
                      )}
                      <div className="d-flex gap-2 justify-content-center mt-2">
                        <button className="btn btn-sm btn-danger" onClick={handleRemoveLogo}>
                          <i className="fas fa-trash me-1"></i> Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    // ✅ Select State - SAME as NIN/CAC
                    <>
                      <i className="fas fa-cloud-upload-alt"></i>
                      <h6>Drag & drop or click to browse</h6>
                      <p className="text-muted small">JPG, PNG, GIF, SVG • Max 2MB</p>
                      <input 
                        type="file" 
                        id="logo-upload" 
                        className="d-none" 
                        accept="image/jpeg,image/png,image/gif,image/svg+xml"
                        onChange={handleSelectLogoFile}
                      />
                      <label htmlFor="logo-upload" className="btn btn-secondary mt-2" style={{ cursor: 'pointer' }}>
                        <i className="fas fa-folder-open me-1"></i> Select Logo File
                      </label>
                    </>
                  )}
                  
                  {/* ✅ Upload Button - SAME as NIN/CAC */}
                  {brandLogo.file && !brandLogo.uploaded && !brandLogo.uploading && (
                    <button 
                      className="btn btn-primary mt-3" 
                      style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}
                      onClick={handleUploadLogo}
                    >
                      <i className="fas fa-upload me-1"></i> Upload Logo
                    </button>
                  )}
                  
                  {/* ✅ Uploading State - SAME as NIN/CAC */}
                  {brandLogo.uploading && (
                    <button className="btn btn-primary mt-3" disabled style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Uploading...
                    </button>
                  )}
                </div>
              ) : (
                // ✅ Uploaded State - SAME as NIN/CAC
                <div className="uploaded-state text-center py-3">
                  <i className="fas fa-check-circle text-success fa-2x mb-2"></i>
                  <p className="mb-0 text-muted small">Logo uploaded successfully</p>
                  {brandLogo.name && (
                    <small className="d-block text-muted mt-1">{brandLogo.name}</small>
                  )}
                  {brandLogo.path && (
                    <img 
                      src={`${API_BASE}${brandLogo.path}`} 
                      alt="Company Logo" 
                      className="mt-2" 
                      style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }}
                    />
                  )}
                  <button className="btn btn-sm btn-outline-secondary mt-2" onClick={handleRemoveLogo}>
                    <i className="fas fa-redo me-1"></i> Replace Logo
                  </button>
                </div>
              )}
            </div>

            <div className="info-box mt-4">
              <i className="fas fa-lightbulb"></i>
              <p className="mb-0">Your logo will appear on invoices, reports, and customer-facing documents. Use a transparent PNG for best results.</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ✅ Render Tab Content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic-details':
        return (
          <div className="settings-content">
            <div className="settings-layout">
              <div className="settings-sidebar">
                {leftMenuItems.map(item => (
                  <button
                    key={item.id}
                    className={`settings-menu-item ${activeSection === item.id ? 'active' : ''}`}
                    onClick={() => setActiveSection(item.id)}
                  >
                    <span>{item.label}</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                ))}
              </div>
              <div className="settings-main">
                <div className="section-header">
                  <h4>{getSectionTitle()}</h4>
                  {!isEditing && activeSection === 'business-info' && (
                    <button className="btn-edit" onClick={() => setIsEditing(true)}>
                      <i className="fas fa-edit"></i> Edit
                    </button>
                  )}
                  {isEditing && (
                    <button className="btn-edit cancel" onClick={() => setIsEditing(false)}>
                      <i className="fas fa-times"></i> Cancel
                    </button>
                  )}
                </div>
                {renderBasicDetailsContent()}
              </div>
            </div>
          </div>
        );

      case 'password-security':
        return (
          <div className="settings-content">
            <div className="card">
              <div className="card-body p-4">
                <h5 className="mb-4">Password & Security</h5>
                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <input type="password" className="form-control" />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input type="password" className="form-control" />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input type="password" className="form-control" />
                </div>
                <button className="btn btn-primary" style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}>Update Password</button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="settings-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* SIDEBAR */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="brand-logo">
            <img src="images/fsl_logo.png" alt="Logo" style={{ height: '50px' }} />
          </Link>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((section, idx) => (
            <div key={idx} className="nav-section">
              <h6 className="nav-section-title">{section.section}</h6>
              <ul className="nav-list">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx}>
                    <Link to={item.path || '#'} className={`nav-link ${item.active ? 'active' : ''}`}
                      onClick={(e) => item.path && handleNavClick(item.path, e)}>
                      <i className={`fas ${item.icon} me-2`}></i>{item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="nav-section">
            <h6 className="nav-section-title">ACCOUNT</h6>
            <ul className="nav-list">
              <li><Link to="/profile" className="nav-link"><i className="fas fa-user me-2"></i>Profile</Link></li>
              <li>
                <button className="nav-link logout-btn" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt me-2"></i>Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)}><i className="fas fa-bars"></i></button>
            <div className="page-title">
              <i className="fas fa-arrow-left me-2" style={{ cursor: 'pointer' }} onClick={() => navigate(-1)}></i>
              <h4 className="mb-0">Settings</h4>
            </div>
          </div>
          <div className="header-right">
            <div className="country-selector"><img src="https://flagcdn.com/w40/ng.png" alt="Nigeria" width="24" /><span>Nigeria</span><i className="fas fa-chevron-down ms-2"></i></div>
            <button className="header-icon"><i className="fas fa-search"></i></button>
            <button className="header-icon"><i className="fas fa-cog"></i></button>
            <button className="header-icon"><i className="fas fa-bell"></i><span className="badge">3</span></button>
            <div className="user-avatar"><span>OO</span></div>
          </div>
        </header>

        <div className="content-wrapper">
          {/* Settings Tabs */}
          <div className="settings-tabs">
            {settingsTabs.map(tab => (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                style={activeTab === tab.id ? { backgroundColor: '#F89423', color: 'white' } : {}}
              >
                <i className={`fas ${tab.icon}`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {renderTabContent()}
        </div>
      </div>

      {/* ✅ Custom CSS - Updated with logo preview styles */}
      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        
        .settings-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        
        /* Sidebar Styles */
        .sidebar { width: 260px; background: white; color: #333; position: fixed; left: 0; top: 0; height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000; border-right: 1px solid #e5e7eb; }
        .sidebar-header { padding: 20px; border-bottom: 1px solid #e5e7eb; }
        .brand-logo { color: #333; font-size: 24px; font-weight: 700; text-decoration: none; display: flex; align-items: center; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 25px; }
        .nav-section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #F89423; padding: 0 20px; margin-bottom: 10px; }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-link { display: flex; align-items: center; padding: 12px 20px; color: #333; text-decoration: none; transition: all 0.3s ease; font-weight: 500; }
        .nav-link:hover, .nav-link.active { background-color: #F89423; color: white; border-left: 4px solid white; }
        .sidebar-footer { margin-top: auto; padding-bottom: 20px; }
        
        /* Main Content */
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .top-header { background: white; padding: 15px 30px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100; }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .page-title { display: flex; align-items: center; color: #1f2937; }
        .header-right { display: flex; align-items: center; gap: 15px; }
        .country-selector { display: flex; align-items: center; padding: 8px 15px; background: #f3f4f6; border-radius: 8px; cursor: pointer; font-weight: 500; }
        .header-icon { width: 40px; height: 40px; border-radius: 50%; border: none; background: #f3f4f6; color: #6b7280; cursor: pointer; position: relative; transition: all 0.3s ease; }
        .header-icon:hover { background: #e5e7eb; color: #F89423; }
        .header-icon .badge { position: absolute; top: 5px; right: 5px; background: #F89423; color: white; font-size: 10px; padding: 2px 5px; border-radius: 10px; }
        .user-avatar { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, #F89423 0%, #F89423 100%); color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; cursor: pointer; }
        
        .content-wrapper { padding: 30px; flex: 1; }
        
        /* Settings Tabs */
        .settings-tabs { display: flex; gap: 10px; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #e5e7eb; }
        .settings-tab { display: flex; align-items: center; gap: 8px; padding: 10px 20px; border: none; background: #f3f4f6; border-radius: 8px; cursor: pointer; font-weight: 500; color: #6b7280; transition: all 0.2s ease; }
        .settings-tab:hover { background: #e5e7eb; }
        .settings-tab.active { background: #F89423; color: white; }
        
        /* Settings Layout */
        .settings-layout { display: flex; gap: 24px; }
        .settings-sidebar { width: 280px; background: white; border-radius: 12px; border: 1px solid #e5e7eb; padding: 16px; height: fit-content; }
        .settings-menu-item { display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 12px 16px; border: none; background: none; text-align: left; cursor: pointer; border-radius: 8px; color: #374151; font-weight: 500; transition: all 0.2s ease; }
        .settings-menu-item:hover { background: #f9fafb; }
        .settings-menu-item.active { background: #fef3e2; color: #F89423; font-weight: 600; }
        .settings-main { flex: 1; background: white; border-radius: 12px; border: 1px solid #e5e7eb; padding: 24px; }
        
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #e5e7eb; }
        .btn-edit { display: flex; align-items: center; gap: 6px; padding: 8px 16px; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 8px; color: #374151; cursor: pointer; font-weight: 500; transition: all 0.2s ease; }
        .btn-edit:hover { background: #e5e7eb; }
        .btn-edit.cancel { background: #fee2e2; border-color: #fecaca; color: #ef4444; }
        .btn-edit.cancel:hover { background: #fecaca; }
        
        /* Form Styles */
        .form-label { display: block; margin-bottom: 6px; font-weight: 600; color: #374151; font-size: 14px; }
        .form-control, .form-select { padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; width: 100%; transition: all 0.2s ease; }
        .form-control:focus, .form-select:focus { border-color: #F89423; box-shadow: 0 0 0 3px rgba(248, 148, 35, 0.15); outline: none; }
        .form-control:disabled, .form-select:disabled { background-color: #f3f4f6; cursor: not-allowed; opacity: 0.8; }
        .form-control.textarea { min-height: 100px; resize: vertical; }
        .form-row { display: flex; gap: 20px; margin-bottom: 20px; }
        .form-group { flex: 1; margin-bottom: 20px; }
        
        .info-box { display: flex; align-items: flex-start; gap: 12px; padding: 16px; background: #fef3e2; border-radius: 8px; margin: 20px 0; }
        .info-box i { color: #F89423; margin-top: 2px; }
        .info-box p { margin: 0; color: #92400e; font-size: 14px; }
        
        .form-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px; padding-top: 24px; border-top: 1px solid #e5e7eb; }
        
        /* Upload Area */
        .upload-area { border: 2px dashed #d1d5db; border-radius: 12px; padding: 30px; text-align: center; background: #f9fafb; transition: all 0.2s ease; }
        .upload-area:hover { border-color: #F89423; background: #fffbf5; }
        .upload-area i { font-size: 48px; color: #9ca3af; margin-bottom: 16px; }
        .upload-area h6 { margin: 0 0 8px; color: #374151; }
        .upload-area p { margin: 0; color: #6b7280; font-size: 13px; }
        
        /* Logo Upload */
        .logo-upload { display: flex; align-items: center; gap: 20px; }
        .logo-preview { width: 120px; height: 120px; border: 2px dashed #d1d5db; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: #f9fafb; }
        .logo-preview i { font-size: 40px; color: #9ca3af; }
        
        /* KYC Document Upload Styles */
        .document-upload-card { background: white; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; }
        .document-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #f3f4f6; }
        .document-header h6 { color: #1f2937; font-weight: 600; }
        .preview-container { text-align: center; }
        .document-preview { max-width: 100%; max-height: 200px; border-radius: 8px; margin-bottom: 12px; border: 1px solid #e5e7eb; object-fit: contain; }
        .file-preview { padding: 20px; background: #f9fafb; border-radius: 8px; margin-bottom: 12px; display: flex; flex-direction: column; align-items: center; }
        .uploaded-state { background: #f0fdf4; border: 1px solid #86efac; border-radius: 8px; }
        
        /* Password & Security */
        .card { border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); background: white; border: none; }
        
        /* Buttons */
        .btn { padding: 10px 20px; border-radius: 8px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; border: none; font-size: 14px; }
        .btn-primary { background: #F89423; color: white; }
        .btn-primary:hover { background: #ea8a1a; }
        .btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }
        .btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #d1d5db; }
        .btn-secondary:hover { background: #e5e7eb; }
        .btn-outline-secondary { background: white; color: #6b7280; border: 1px solid #d1d5db; }
        .btn-outline-secondary:hover { background: #f9fafb; }
        .btn-sm { padding: 6px 12px; font-size: 13px; }
        .btn-danger { background: #fee2e2; color: #ef4444; border: 1px solid #fecaca; }
        .btn-danger:hover { background: #fecaca; }
        
        /* Loading Spinner */
        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
          border-width: 0.15em;
          vertical-align: text-bottom;
        }
        
        /* Responsive */
        @media (max-width: 992px) {
          .sidebar { transform: translateX(-100%); }
          .sidebar.sidebar-open { transform: translateX(0); }
          .main-content { margin-left: 0; }
          .settings-layout { flex-direction: column; }
          .settings-sidebar { width: 100%; }
        }
        @media (max-width: 768px) {
          .content-wrapper { padding: 15px; }
          .top-header { padding: 15px; }
          .header-right .country-selector span { display: none; }
          .form-row { flex-direction: column; gap: 0; }
          .form-group { margin-bottom: 16px; }
          .document-header { flex-direction: column; align-items: flex-start; gap: 8px; }
        }
      `}</style>
    </div>
  );
}

export default Settings;